import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  closeLead,
  closeReminderLead,
  isErpApiSuccess,
  listPendingReminders,
  nextLeadFollowup,
  nextReminderFollowup,
  partialPaymentLead,
  partialPaymentReminder,
  snoozeReminder,
} from "../erpClient";
import { getDefaultBranchId } from "../utils/leadBranch";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";

const REMINDERS_FETCH_DEDUPE_MS = 2000;

let pendingRemindersPromise = null;
let pendingRemindersCache = null;
let pendingRemindersCacheKey = null;
let pendingRemindersCacheAt = 0;

function todayYmdLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDt(v) {
  if (v == null || v === "") return "—";
  const s = String(v);
  return s.length > 16 ? s.slice(0, 16).replace("T", " ") : s.replace("T", " ");
}

function datePart(v) {
  if (v == null || v === "") return "";
  return String(v).slice(0, 10);
}

function isOverdueFollowup(v) {
  const d = datePart(v);
  if (!d) return false;
  return d < todayYmdLocal();
}

function toDatetimeLocalValue(v) {
  if (!v) {
    const n = new Date();
    n.setMinutes(n.getMinutes() - n.getTimezoneOffset());
    return n.toISOString().slice(0, 16);
  }
  return String(v).replace(" ", "T").slice(0, 16);
}

function fromDatetimeLocal(s) {
  if (!s) return "";
  return s.includes(" ") ? s : `${s.replace("T", " ")}:00`.slice(0, 19);
}

function fromDateOnly(s) {
  if (!s) return "";
  return `${s} 09:00:00`;
}

function normalizeInboxRow(raw) {
  if (!raw || typeof raw !== "object") return null;
  const leadId = Number(raw.lead_id ?? raw.leadId ?? raw.id);
  if (!Number.isFinite(leadId) || leadId <= 0) return null;
  return {
    lead_id: leadId,
    name: raw.name || raw.customer_name || "",
    mobile: raw.mobile || raw.customer_mobile || "",
    title: raw.title || raw.lead_title || "",
    lead_type: raw.lead_type || raw.leadType || "",
    due_amount:
      raw.due_amount != null
        ? raw.due_amount
        : raw.amount != null
          ? raw.amount
          : null,
    next_followup_at:
      raw.next_followup_at ||
      raw.nextFollowupAt ||
      raw.scheduled_at ||
      raw.scheduledAt ||
      "",
    entity_type: raw.entity_type || raw.entityType || "",
    entity_id: raw.entity_id ?? raw.entityId ?? null,
    status: raw.status || "",
  };
}

async function requestInboxReminders(branchId, { force = false } = {}) {
  const key = `inbox:${branchId}`;
  const now = Date.now();

  if (!force) {
    if (pendingRemindersPromise) return pendingRemindersPromise;
    if (
      pendingRemindersCache &&
      pendingRemindersCacheKey === key &&
      now - pendingRemindersCacheAt < REMINDERS_FETCH_DEDUPE_MS
    ) {
      return pendingRemindersCache;
    }
  } else {
    pendingRemindersPromise = null;
    pendingRemindersCache = null;
    pendingRemindersCacheKey = null;
    pendingRemindersCacheAt = 0;
  }

  pendingRemindersPromise = listPendingReminders({
    branch_id: branchId,
    filter: "inbox",
  })
    .then((res) => {
      if (isErpApiSuccess(res)) {
        pendingRemindersCache = res;
        pendingRemindersCacheKey = key;
        pendingRemindersCacheAt = Date.now();
      }
      return res;
    })
    .finally(() => {
      pendingRemindersPromise = null;
    });

  return pendingRemindersPromise;
}

function entityPath(row) {
  const type = String(row.entity_type || "").toUpperCase();
  const id = row.entity_id;
  if (id == null || id === "") return null;
  if (type === "SALE") {
    return {
      pathname: "/editinvoice",
      state: { selectedItemIds: [id] },
    };
  }
  if (type === "CUSTOM_ORDER") {
    return {
      pathname: "/editcustomorderentry",
      state: { customOrderId: id },
    };
  }
  if (type === "REPAIR") {
    return {
      pathname: "/editrepairingorder",
      state: { repairingOrderId: id },
    };
  }
  return null;
}

function RemindersQueue() {
  const navigate = useNavigate();
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const branchId = getDefaultBranchId();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [actionRow, setActionRow] = useState(null);
  const [actionKind, setActionKind] = useState(null); // next | snooze | partial | close
  const [nextAt, setNextAt] = useState("");
  const [snoozeAt, setSnoozeAt] = useState("");
  const [partialDue, setPartialDue] = useState("");
  const [partialNextAt, setPartialNextAt] = useState("");
  const [actionNote, setActionNote] = useState("");
  const [closeReason, setCloseReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(
    async ({ force = false } = {}) => {
      setLoading(true);
      try {
        const res = await requestInboxReminders(branchId, { force });
        if (!isErpApiSuccess(res)) {
          setRows([]);
          toast.error(res?.message || "Failed to load reminders");
          return;
        }
        const data = res.data;
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.rows)
            ? data.rows
            : [];
        setRows(list.map(normalizeInboxRow).filter(Boolean));
      } catch (err) {
        console.error(err);
        setRows([]);
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load reminders",
        );
      } finally {
        setLoading(false);
      }
    },
    [branchId],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const openAction = (kind, row) => {
    setActionKind(kind);
    setActionRow(row);
    setActionNote("");
    setCloseReason("");
    const base = toDatetimeLocalValue(row.next_followup_at);
    setNextAt(base);
    setSnoozeAt(base);
    setPartialNextAt(datePart(row.next_followup_at) || todayYmdLocal());
    setPartialDue(
      row.due_amount != null && row.due_amount !== ""
        ? String(row.due_amount)
        : "",
    );
  };

  const closeAction = () => {
    setActionKind(null);
    setActionRow(null);
    setSubmitting(false);
  };

  const submitAction = async () => {
    if (!actionRow || !actionKind || submitting) return;
    const leadId = actionRow.lead_id;
    setSubmitting(true);
    try {
      let res;
      if (actionKind === "next") {
        if (!nextAt) {
          toast.error("Next follow-up date is required");
          setSubmitting(false);
          return;
        }
        const nextPayload = {
          lead_id: leadId,
          next_followup_at: fromDatetimeLocal(nextAt),
          ...(actionNote.trim() ? { note: actionNote.trim() } : {}),
        };
        res = await nextLeadFollowup(nextPayload);
        if (!isErpApiSuccess(res)) {
          res = await nextReminderFollowup(nextPayload);
        }
      } else if (actionKind === "snooze") {
        if (!snoozeAt) {
          toast.error("Snooze time is required");
          setSubmitting(false);
          return;
        }
        res = await snoozeReminder({
          lead_id: leadId,
          scheduled_at: fromDatetimeLocal(snoozeAt),
        });
      } else if (actionKind === "partial") {
        if (!partialNextAt) {
          toast.error("Next follow-up date is required for partial payment");
          setSubmitting(false);
          return;
        }
        const partialPayload = {
          lead_id: leadId,
          next_followup_at: fromDateOnly(partialNextAt),
          ...(partialDue.trim() !== ""
            ? { due_amount: Number(partialDue) }
            : {}),
          ...(actionNote.trim() ? { note: actionNote.trim() } : {}),
        };
        res = await partialPaymentLead(partialPayload);
        if (!isErpApiSuccess(res)) {
          res = await partialPaymentReminder(partialPayload);
        }
      } else if (actionKind === "close") {
        const reason = closeReason.trim();
        if (!reason) {
          toast.error("Closing reason is required");
          setSubmitting(false);
          return;
        }
        res = await closeLead({
          lead_id: leadId,
          closed_reason: reason,
        });
        if (!isErpApiSuccess(res)) {
          res = await closeReminderLead({
            lead_id: leadId,
            closed_reason: reason,
          });
        }
      }

      if (!isErpApiSuccess(res)) {
        toast.error(res?.message || "Action failed");
        setSubmitting(false);
        return;
      }
      toast.success(
        actionKind === "close"
          ? "Lead closed"
          : actionKind === "snooze"
            ? "Snoozed"
            : actionKind === "partial"
              ? "Partial payment saved"
              : "Next follow-up set",
      );
      closeAction();
      await load({ force: true });
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Action failed",
      );
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="body-wrapper">
        <div className="container-fluid">
          <div
            className={`card w-100 position-relative overflow-hidden ${cardClass}`}
          >
            <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <h4 className="card-title mb-0">Reminders</h4>
                <div className="small text-muted mt-1">
                  Today + overdue follow-ups
                </div>
              </div>
              <Button
                variant="outline-secondary"
                className={buttonClass}
                onClick={() => load({ force: true })}
              >
                Refresh
              </Button>
            </div>
            <div className="card-body p-4">
              <div className={`table-responsive border ${cardClass}`}>
                <table
                  className={`table text-nowrap mb-0 align-middle ${tableClass}`}
                >
                  <thead className="text-dark fs-4">
                    <tr>
                      <th>Name</th>
                      <th>Mobile</th>
                      <th>Title</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Next follow-up</th>
                      <th>Entity</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={8} className="text-center py-4">
                          Loading…
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={8}
                          className="text-center text-muted py-4"
                        >
                          No reminders due today
                        </td>
                      </tr>
                    ) : (
                      rows.map((r) => {
                        const overdue = isOverdueFollowup(r.next_followup_at);
                        const link = entityPath(r);
                        return (
                          <tr
                            key={r.lead_id}
                            className={overdue ? "table-danger" : undefined}
                          >
                            <td>
                              <button
                                type="button"
                                className={`btn btn-link p-0 text-start ${buttonClass}`}
                                onClick={() =>
                                  navigate(`/leads/${r.lead_id}`)
                                }
                              >
                                {r.name || "—"}
                              </button>
                              {overdue && (
                                <span className="badge bg-danger ms-2">
                                  Overdue
                                </span>
                              )}
                            </td>
                            <td>{r.mobile || "—"}</td>
                            <td>{r.title || "—"}</td>
                            <td>{r.lead_type || "—"}</td>
                            <td>
                              {r.due_amount != null && r.due_amount !== ""
                                ? r.due_amount
                                : "—"}
                            </td>
                            <td>{formatDt(r.next_followup_at)}</td>
                            <td className="small">
                              {link ? (
                                <button
                                  type="button"
                                  className={`btn btn-link p-0 ${buttonClass}`}
                                  onClick={() =>
                                    navigate(link.pathname, {
                                      state: link.state,
                                    })
                                  }
                                >
                                  {r.entity_type || "Entity"} #{r.entity_id}
                                </button>
                              ) : r.entity_type ? (
                                `${r.entity_type}${
                                  r.entity_id != null ? ` #${r.entity_id}` : ""
                                }`
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="text-end text-nowrap">
                              <button
                                type="button"
                                className={`btn btn-sm btn-outline-primary me-1 ${buttonClass}`}
                                onClick={() => openAction("next", r)}
                              >
                                Next
                              </button>
                              <button
                                type="button"
                                className={`btn btn-sm btn-outline-secondary me-1 ${buttonClass}`}
                                onClick={() => openAction("snooze", r)}
                              >
                                Snooze
                              </button>
                              <button
                                type="button"
                                className={`btn btn-sm btn-outline-info me-1 ${buttonClass}`}
                                onClick={() => openAction("partial", r)}
                              >
                                Partial
                              </button>
                              <button
                                type="button"
                                className={`btn btn-sm btn-outline-danger ${buttonClass}`}
                                onClick={() => openAction("close", r)}
                              >
                                Close
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={actionKind != null} onHide={closeAction} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {actionKind === "next"
              ? "Next follow-up"
              : actionKind === "snooze"
                ? "Snooze"
                : actionKind === "partial"
                  ? "Partial payment"
                  : "Close lead"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionRow && (
            <p className="small text-muted mb-3">
              {actionRow.title || actionRow.name || `Lead #${actionRow.lead_id}`}
            </p>
          )}
          {actionKind === "next" && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Next follow-up</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={nextAt}
                  onChange={(e) => setNextAt(e.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Note (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={actionNote}
                  onChange={(e) => setActionNote(e.target.value)}
                />
              </Form.Group>
            </>
          )}
          {actionKind === "snooze" && (
            <Form.Group>
              <Form.Label>Snooze until</Form.Label>
              <Form.Control
                type="datetime-local"
                value={snoozeAt}
                onChange={(e) => setSnoozeAt(e.target.value)}
              />
            </Form.Group>
          )}
          {actionKind === "partial" && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Remaining due amount (optional)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  step="0.01"
                  value={partialDue}
                  onChange={(e) => setPartialDue(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Next follow-up date (required)</Form.Label>
                <Form.Control
                  type="date"
                  value={partialNextAt}
                  onChange={(e) => setPartialNextAt(e.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Note (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={actionNote}
                  onChange={(e) => setActionNote(e.target.value)}
                />
              </Form.Group>
            </>
          )}
          {actionKind === "close" && (
            <Form.Group>
              <Form.Label>Closing reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeAction} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submitAction} disabled={submitting}>
            {submitting ? "Saving…" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default RemindersQueue;
